// Spec 081 — vocabulário do CONTADOR: o sentido que ele declara, a equivalência que converte a
// leitura para a unidade da linha, os refinamentos que não dependem do banco e a projeção de
// leitura. Separado de `parameters.ts` pelo teto de 200 linhas efetivas, não por camada nova.
import { z } from "zod";
import { LossTypeSchema } from "./common.js";

// Spec 081 — o SENTIDO do contador. Substitui `influencesProduction`, que respondia só a
// `PRODUCES`: com três sentidos, o booleano deixaria de responder a dois deles, e manter os dois
// campos criaria segunda fonte de verdade a sincronizar em todo create e update.
export const ProductionRoleSchema = z.enum(["PRODUCES", "CONSUMES", "SCRAPS"]);
export type ProductionRole = z.infer<typeof ProductionRoleSchema>;

// Equivalência: quanto vale uma unidade contada na unidade da linha. Positiva e finita — zero
// produziria quantidade nula e negativo produção negativa, os dois sem nada acusar.
export const UnitFactorSchema = z.number().positive().finite();

// Spec 081 — refinamentos do contador que NÃO dependem do banco, e por isso rodam no formulário e
// na rota (400) em vez de virarem viagem ao servidor.
export const counterRoleRefine = <
  T extends {
    category?: string;
    productionRole?: unknown;
    lossType?: unknown;
    unitFactor?: unknown;
  },
>(
  b: T
): boolean => {
  // 1. papel só vale em COUNTER.
  if (b.productionRole != null && b.category !== "COUNTER") return false;
  // 2. tipo de perda ⟺ papel SCRAPS.
  if ((b.lossType != null) !== (b.productionRole === "SCRAPS")) return false;
  // 3. sobreposição de equivalência exige produto — sem ele não há de que herdar nem o que sobrepor.
  if (
    b.unitFactor != null &&
    !("productId" in b && (b as { productId?: unknown }).productId != null)
  )
    return false;
  return true;
};
export const COUNTER_ROLE_REFINE_MESSAGE: { message: string; path: (string | number)[] } = {
  message:
    "Contador inválido: o sentido só vale em contador, o tipo de perda acompanha o sentido " +
    "Refugo, e a equivalência própria exige um produto.",
  path: ["productionRole"],
};

// Spec 081 — a projeção de leitura do contador. `effectiveUnitFactor` e `unitFactorIsOverride` são
// campos IRMÃOS: sem o segundo a lista não distingue equivalência herdada de sobreposta, que é
// justamente o que FR-033 pede para marcar. `accumulated*` idem — um número sem dizer de qual
// execução veio não é informação, e nulo NUNCA é zero (FR-033a).
export const CounterProjectionSchema = z.object({
  productionRole: ProductionRoleSchema.nullable(),
  product: z
    .object({ id: z.string().uuid(), name: z.string(), countingUnit: z.string() })
    .nullable(),
  lossType: LossTypeSchema.nullable(),
  effectiveUnitFactor: z.number().positive().finite(),
  unitFactorIsOverride: z.boolean(),
  accumulatedInLineUnit: z.number().finite().nullable(),
  accumulatedScope: z.enum(["active", "last"]).nullable(),
});
export type CounterProjection = z.infer<typeof CounterProjectionSchema>;
